/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  InputAdornment,
  Stack,
  useTheme,
  MenuItem,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Event as EventIcon,
  VpnKey as VpnKeyIcon,
  Person as PersonIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  fetchRolePermissions,
  assignRolePermissions,
} from "store/features/admin/roleManagement/roleAccess/roleAccessActions";
import {
  selectRolePermissions,
  selectRolePermissionsLoading,
} from "store/features/admin/roleManagement/roleAccess/roleAccessSelectors";
import AddRoleAccessModal from "./AddRoleAccessModal";
import roleAccessSvg from "assets/folders/role-access.svg";

const RoleAccess = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const rolePermissions = useSelector(selectRolePermissions);
  const loading = useSelector(selectRolePermissionsLoading);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchRolePermissions());
  }, [dispatch]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, roleFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get unique roles for filter
  const uniqueRoles = [];
  const rolesMap = new Map();
  rolePermissions.forEach((item) => {
    if (!rolesMap.has(item.role_id)) {
      rolesMap.set(item.role_id, {
        role_id: item.role_id,
        role_name: item.role_name,
        role_code: item.role_code,
      });
      uniqueRoles.push({
        role_id: item.role_id,
        role_name: item.role_name,
        role_code: item.role_code,
      });
    }
  });

  // Filter permissions by role
  const filteredPermissions = rolePermissions.filter((permission) => {
    const matchesRole =
      roleFilter === "all" || permission.role_id === parseInt(roleFilter);

    const matchesSearch =
      searchTerm === "" ||
      permission.resource?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.description
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      permission.role_name?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesRole && matchesSearch;
  });

  // Group permissions by role for display
  const permissionsByRole = filteredPermissions.reduce(
    (grouped, permission) => {
      const roleId = permission.role_id;
      if (!grouped[roleId]) {
        grouped[roleId] = {
          role_id: permission.role_id,
          role_name: permission.role_name,
          role_code: permission.role_code,
          permissions: [],
        };
      }
      grouped[roleId].permissions.push(permission);
      return grouped;
    },
    {},
  );

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setShowCreateModal(true);
  };

  const getActionColor = (action) => {
    switch (action) {
      case "create":
        return "#10b981";
      case "view":
        return "#3b82f6";
      case "update":
        return "#f59e0b";
      case "delete":
        return "#ef4444";
      case "manage":
        return "#8b5cf6";
      case "checkin":
        return "#06b6d4";
      case "checkout":
        return "#06b6d4";
      case "apply":
        return "#14b8a6";
      case "approve":
        return "#f97316";
      case "generate":
        return "#ec4899";
      case "send":
        return "#6366f1";
      default:
        return "#6b7280";
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={roleAccessSvg}
              alt="Role Access"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Role Access Management
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage permissions assigned to different roles
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by role, resource or action..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            width: { xs: "100%", sm: 250 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          label="Filter by Role"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 200 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
            "& .MuiInputLabel-root": {
              color: "text.secondary",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "primary.main",
            },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  bgcolor: "background.paper",
                },
              },
            },
          }}
        >
          <MenuItem value="all">All Roles</MenuItem>
          {uniqueRoles.map((role) => (
            <MenuItem key={role.role_id} value={role.role_id}>
              {role.role_name}
            </MenuItem>
          ))}
        </TextField>

        {roleFilter !== "all" && (
          <Button
            onClick={() => setRoleFilter("all")}
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              color: "primary.main",
              borderColor: "#f59e0b",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#d97706",
                backgroundColor: "rgba(245,158,11,0.1)",
              },
            }}
            variant="outlined"
          >
            Clear Filter
          </Button>
        )}
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "primary.main",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading role permissions...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Role
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Permission
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Resource / Action
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Assigned At
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(permissionsByRole).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <VpnKeyIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        No role permissions found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  Object.values(permissionsByRole).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).flatMap(
                    (roleGroup, groupIndex) =>
                      roleGroup.permissions.map((permission, permIndex) => (
                        <TableRow
                          key={`${permission.role_id}-${permission.permission_id}`}
                          component={motion.tr}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: (groupIndex + permIndex) * 0.05,
                          }}
                          sx={{
                            "&:hover": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(30,41,59,0.5)"
                                  : "#f9fafb",
                            },
                          }}
                        >
                          {permIndex === 0 && (
                            <>
                              <TableCell
                                rowSpan={roleGroup.permissions.length}
                                sx={{
                                  verticalAlign: "top",
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(30,41,59,0.3)"
                                      : "#fafafa",
                                  borderRight: `1px solid ${
                                    theme.palette.mode === "dark"
                                      ? "#1e293b"
                                      : "#f3f4f6"
                                  }`,
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1.5}
                                  alignItems="center"
                                >
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: "12px",
                                      background:
                                        "linear-gradient(135deg, #f59e0b20 0%, #d9770620 100%)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <PersonIcon
                                      sx={{ fontSize: 24, color: "primary.main" }}
                                    />
                                  </Box>
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontWeight: 600,
                                        color: "text.primary",
                                      }}
                                    >
                                      {roleGroup.role_name}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "0.7rem",
                                        fontFamily: "monospace",
                                        color: "primary.main",
                                      }}
                                    >
                                      {roleGroup.role_code}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                            </>
                          )}
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "8px",
                                  backgroundColor: "rgba(245, 158, 11, 0.1)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <VpnKeyIcon
                                  sx={{ fontSize: 16, color: "primary.main" }}
                                />
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#d1d5db"
                                      : "#4b5563",
                                }}
                              >
                                {permission.description ||
                                  `${permission.resource} ${permission.action}`}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  textTransform: "capitalize",
                                  color: "text.primary",
                                }}
                              >
                                {permission.resource}
                              </Typography>
                              <Chip
                                label={permission.action}
                                size="small"
                                sx={{
                                  backgroundColor: `${getActionColor(
                                    permission.action,
                                  )}20`,
                                  color: getActionColor(permission.action),
                                  fontWeight: 600,
                                  fontSize: "0.65rem",
                                  textTransform: "capitalize",
                                  height: "20px",
                                  width: "fit-content",
                                }}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <EventIcon
                                sx={{ fontSize: 14, color: "primary.main" }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#d1d5db"
                                      : "#4b5563",
                                }}
                              >
                                {formatDateTime(permission.created_at)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            {permIndex === 0 && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() =>
                                  handleEditRole({
                                    role_id: roleGroup.role_id,
                                    role_name: roleGroup.role_name,
                                    role_code: roleGroup.role_code,
                                  })
                                }
                                sx={{
                                  textTransform: "none",
                                  borderRadius: "8px",
                                  borderColor: "#f59e0b",
                                  color: "primary.main",
                                  "&:hover": {
                                    borderColor: "#d97706",
                                    backgroundColor: "rgba(245,158,11,0.1)",
                                  },
                                }}
                              >
                                Edit Permissions
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )),
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={Object.keys(permissionsByRole).length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Role Access Count Summary */}
      {!loading && rolePermissions.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">
            Total Role Permissions: {rolePermissions.length}
          </Typography>
          <Typography variant="body2">
            Showing: {filteredPermissions.length} of {rolePermissions.length}
          </Typography>
        </Stack>
      )}

      {/* Empty State for No Search Results */}
      {!loading &&
        filteredPermissions.length === 0 &&
        rolePermissions.length > 0 && (
          <Paper
            sx={{
              mt: 4,
              p: 4,
              textAlign: "center",
              borderRadius: "16px",
              bgcolor: "background.paper",
            }}
          >
            <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
            <Typography
              sx={{
                color: "text.secondary",
                mb: 2,
              }}
            >
              No role permissions match your search criteria
            </Typography>
            <Button
              onClick={() => {
                setSearchTerm("");
                setRoleFilter("all");
              }}
              sx={{
                color: "primary.main",
                textTransform: "none",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(245,158,11,0.1)"
                      : "#fffbeb",
                },
              }}
            >
              Clear filters
            </Button>
          </Paper>
        )}

      {/* Add/Edit Role Access Modal */}
      <AddRoleAccessModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedRole(null);
        }}
        onSuccess={() => dispatch(fetchRolePermissions())}
        selectedRole={selectedRole}
      />
    </Box>
  );
};

export default RoleAccess;
